'use client';

import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {useCloseOnEscape} from 'common/hooks/close_on_escape';

interface ModalBackdropProps {
  children: React.ReactNode;
  /** If provided, enables dismiss on Escape key and backdrop click. */
  onClose?: () => void;
  /** Overlay z-index. Defaults to 20. */
  zIndex?: number;
  /** Tailwind classes for the backdrop div. Defaults to 'bg-black/40'. */
  className?: string;
  /** aria-label for the dialog element. */
  'aria-label'?: string;
}

export function ModalBackdrop({
  children,
  onClose,
  zIndex = 20,
  className = 'bg-black/40',
  'aria-label': ariaLabel,
}: ModalBackdropProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useCloseOnEscape(onClose);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!mounted) return null;

  return createPortal(
    <div
      className='fixed inset-0 flex items-center justify-center p-4'
      style={{zIndex}}
      aria-modal='true'
      role='dialog'
      aria-label={ariaLabel}>
      <div className={`absolute inset-0 ${className}`} onClick={onClose} />
      {children}
    </div>,
    document.body,
  );
}
